// src/components/common/PaginatedSearchMultiSelect.tsx
//
// Multi-select genérico com busca server-side + paginação infinita.
// Usado quando a lista total é grande demais para carregar em memória
// (produtos, ou catálogos extensos). Para listas pequenas estáticas,
// preferir MultiSelectList.tsx (sem busca/paginação).
//
// Padrão de cascata reativa (ADR-005): debouncedBusca muda → fetchWithBusca
// re-cria via useCallback → usePaginatedFetch.loadData muda → reset muda →
// useEffect interno [reset, enabled] dispara refetch a partir de page 0.
// PROIBIDO adicionar useEffect externo + reset() — anti-pattern bugado.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePaginatedFetch } from '@/hooks/usePaginatedFetch';
import './PaginatedSearchMultiSelect.css';

interface PaginatedSearchMultiSelectProps<T> {
  label: string;
  placeholder: string;
  // Recebe page + termo de busca; retorna shape de Spring Page (adaptado).
  fetchFunction: (page: number, busca: string) => Promise<{ items: T[]; totalItems: number }>;
  selectedItems: T[];
  onSelectionChange: (items: T[]) => void;
  getItemKey: (item: T) => string;
  renderItem: (item: T) => React.ReactNode;
  renderChip: (item: T) => React.ReactNode;
  theme: 'dark' | 'light';
  disabled?: boolean;
  // Quando false, nada é renderizado/buscado. Default true.
  enabled?: boolean;
}

const DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;

const PaginatedSearchMultiSelectInner = <T,>({
  label,
  placeholder,
  fetchFunction,
  selectedItems,
  onSelectionChange,
  getItemKey,
  renderItem,
  renderChip,
  theme,
  disabled,
  enabled = true,
}: PaginatedSearchMultiSelectProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [busca, setBusca] = useState('');
  const [debouncedBusca, setDebouncedBusca] = useState('');
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedBusca(busca), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [busca]);

  // Wrapper que adapta a assinatura (page, currentItems) do usePaginatedFetch
  // para (page, busca) da prop pública; injeta debouncedBusca via closure.
  const fetchWrapped = useCallback(
    async (page: number, _current: T[]) => fetchFunction(page, debouncedBusca),
    [fetchFunction, debouncedBusca]
  );

  const { items, hasMore, isLoading, loadMore } = usePaginatedFetch<T>({
    fetchFunction: fetchWrapped,
    pageSize: PAGE_SIZE,
    enabled: enabled && isOpen,
  });

  // IntersectionObserver para carregar mais ao chegar no fim da lista
  useEffect(() => {
    if (!isOpen || items.length === 0 || !hasMore) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoading) loadMore();
      },
      { threshold: 0.1, rootMargin: '0px 0px 50px 0px' }
    );
    const ref = sentinelRef.current;
    if (ref) obs.observe(ref);
    return () => {
      if (ref) obs.unobserve(ref);
    };
  }, [isOpen, items.length, hasMore, isLoading, loadMore]);

  // Click outside fecha o dropdown
  useEffect(() => {
    if (!isOpen) return;
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isOpen]);

  const isItemSelected = useCallback(
    (item: T) => {
      const key = getItemKey(item);
      return selectedItems.some((s) => getItemKey(s) === key);
    },
    [getItemKey, selectedItems]
  );

  const toggleItem = useCallback(
    (item: T) => {
      if (disabled) return;
      const key = getItemKey(item);
      if (selectedItems.some((s) => getItemKey(s) === key)) {
        onSelectionChange(selectedItems.filter((s) => getItemKey(s) !== key));
      } else {
        onSelectionChange([...selectedItems, item]);
      }
    },
    [disabled, getItemKey, onSelectionChange, selectedItems]
  );

  const removeChip = useCallback(
    (item: T) => {
      if (disabled) return;
      const key = getItemKey(item);
      onSelectionChange(selectedItems.filter((s) => getItemKey(s) !== key));
    },
    [disabled, getItemKey, onSelectionChange, selectedItems]
  );

  if (!enabled) return null;

  return (
    <div
      className={`paginated-search-multi-select ${theme} ${disabled ? 'disabled' : ''}`}
      ref={containerRef}
    >
      <label className="psms-label">{label}:</label>
      <div className="psms-chips">
        {selectedItems.length === 0 && (
          <span className="psms-chips-empty">{placeholder}</span>
        )}
        {selectedItems.map((item) => (
          <span key={getItemKey(item)} className="psms-chip">
            <span className="psms-chip-content">{renderChip(item)}</span>
            <button
              type="button"
              className="psms-chip-remove"
              onClick={() => removeChip(item)}
              disabled={disabled}
              aria-label="Remover"
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <button
        type="button"
        className="psms-toggle"
        onClick={() => setIsOpen((v) => !v)}
        disabled={disabled}
      >
        {isOpen ? 'Fechar busca' : 'Adicionar / Buscar...'}
      </button>
      {isOpen && (
        <div className="psms-menu">
          <input
            type="text"
            className="psms-search-input"
            placeholder={placeholder}
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            autoFocus
          />
          <div className="psms-list">
            {items.length === 0 && !isLoading && (
              <p className="psms-empty">Nenhum resultado encontrado.</p>
            )}
            {items.map((item) => {
              const key = getItemKey(item);
              const selected = isItemSelected(item);
              return (
                <div
                  key={key}
                  className={`psms-item ${selected ? 'selected' : ''}`}
                  onClick={() => toggleItem(item)}
                  role="option"
                  aria-selected={selected}
                >
                  <input type="checkbox" checked={selected} readOnly tabIndex={-1} />
                  <div className="psms-item-content">{renderItem(item)}</div>
                </div>
              );
            })}
            <div ref={sentinelRef} style={{ height: '8px' }} />
            {isLoading && <p className="psms-loading">Carregando...</p>}
          </div>
        </div>
      )}
    </div>
  );
};

// Cast para preservar a generic <T> no default export
const PaginatedSearchMultiSelect = PaginatedSearchMultiSelectInner as <T>(
  props: PaginatedSearchMultiSelectProps<T>
) => React.ReactElement | null;

export default PaginatedSearchMultiSelect;
